import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { feManagementSystemApi, TokenState } from '../api/fe-management-system-api';
import { useToasterStore } from '../stores/ui/toasterStore';
import { formatElapsedTime } from '../utils/formatters';
import CopyButton from './CopyButton';
import getApiErrorMessage from '../utils/getApiErrorMessage';

const ArchiveManager: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [archivedTokens, setArchivedTokens] = useState<TokenState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const { showToaster } = useToasterStore();

  // 아카이브된 토큰 목록 가져오기
  const fetchArchivedTokens = useCallback(async () => {
    try {
      setIsLoading(true);
      const tokens = await feManagementSystemApi.getArchivedTokenStates();
      setArchivedTokens(tokens);
    } catch (error: any) {
      console.error('Failed to fetch archived tokens:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to load archived tokens.'),
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showToaster]);

  // 모달이 열릴 때 아카이브된 토큰 목록 가져오기
  useEffect(() => {
    if (isVisible) {
      fetchArchivedTokens();
    }
  }, [isVisible, fetchArchivedTokens]);

  // 검색 실행
  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const addresses = await feManagementSystemApi.searchTokens(searchKeyword);
      setSearchResults(addresses);
    } catch (error: any) {
      console.error('Failed to search tokens:', error);
      showToaster(
        getApiErrorMessage(error, 'Search failed.'),
        'error'
      );
    } finally {
      setIsSearching(false);
    }
  };

  // 검색 결과와 아카이브된 토큰을 필터링 및 정렬
  const filteredArchivedTokens = useMemo(() => {
    let result = archivedTokens;

    // 검색 결과가 있으면 검색 결과에 포함된 아카이브된 토큰만 표시
    if (searchResults.length > 0) {
      result = archivedTokens.filter((token) =>
        searchResults.includes(token.tokenAddress)
      );
    } else if (searchKeyword.trim()) {
      // 검색 키워드가 있지만 검색 결과가 없으면 빈 배열
      result = [];
    }

    // 추가로 CA 또는 심볼로 필터링 (클라이언트 사이드)
    if (searchKeyword.trim() && searchResults.length === 0) {
      const keyword = searchKeyword.toLowerCase();
      result = archivedTokens.filter(
        (token) =>
          token.tokenAddress.toLowerCase().includes(keyword) ||
          token.tokenSymbol.toLowerCase().includes(keyword)
      );
    }

    // archivedAt 기준 최신 순으로 정렬
    return result.sort((a, b) => {
      const aDate = a.archivedAt ? new Date(a.archivedAt).getTime() : 0;
      const bDate = b.archivedAt ? new Date(b.archivedAt).getTime() : 0;
      return bDate - aDate; // 내림차순 (최신순)
    });
  }, [archivedTokens, searchResults, searchKeyword]);

  // 복원 버튼 클릭
  const handleRestore = async (tokenAddress: string) => {
    try {
      const token = archivedTokens.find((t) => t.tokenAddress === tokenAddress);
      if (!token) return;

      await feManagementSystemApi.updateTokenState(tokenAddress, {
        isArchived: false,
        memo: token.memo,
      });

      showToaster(`$${token.tokenSymbol} has been restored.`, 'success');
      
      // 목록에서 제거
      setArchivedTokens((prev) =>
        prev.filter((t) => t.tokenAddress !== tokenAddress)
      );
    } catch (error: any) {
      console.error('Failed to restore token:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to restore.'),
        'error'
      );
    }
  };


  const handleCloseModal = () => {
    setIsVisible(false);
    setSearchKeyword('');
    setSearchResults([]);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  return (
    <>
      {/* Archive Management Button */}
      <button
        onClick={() => setIsVisible(true)}
        style={{
          left: -4
        }}
        className="fixed left-4 bottom-0 transform -translate-y-1/2 z-50 flex items-center space-x-2 px-3 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors shadow-lg"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      </button>

      {/* Modal Overlay */}
      {isVisible && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
        >
          {/* Modal Content */}
          <div className="bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white">
                Archived Tokens ({archivedTokens.length})
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search Section */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search by CA or token symbol..."
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
                {searchKeyword && (
                  <button
                    onClick={() => {
                      setSearchKeyword('');
                      setSearchResults([]);
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading ? (
                <div className="text-center py-12 text-gray-400">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Loading archived tokens...</p>
                </div>
              ) : filteredArchivedTokens.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  <p className="text-lg">
                    {searchKeyword ? 'No matching archived tokens found' : 'No Archived Tokens'}
                  </p>
                  <p className="text-sm mt-2">
                    {searchKeyword ? 'Try a different search term' : 'Archived tokens will be displayed here.'}
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {filteredArchivedTokens.map((token) => (
                    <div
                      key={token.tokenAddress}
                      className="flex items-center space-x-4 bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors"
                    >
                      {/* Thumbnail */}
                      <div className="flex-shrink-0">
                       
                          <div className="w-16 h-16 rounded-lg bg-gray-700 border border-gray-600 flex items-center justify-center">
                            <span className="text-gray-500 text-xs">
                              {token.tokenSymbol?.charAt(0) || '?'}
                            </span>
                          </div>
                        
                      </div>

                      {/* Token Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-semibold text-white truncate">
                            {token.tokenSymbol}
                          </h3>
                          <span className="text-sm text-gray-400 truncate">
                            {token.tokenName}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-400">
                          <span className="font-mono">
                            {token.tokenAddress.slice(0, 4)}...{token.tokenAddress.slice(-4)}
                          </span>
                          <CopyButton
                            text={token.tokenAddress}
                            title="Copy token address"
                            onCopy={() => showToaster('Token address copied to clipboard!', 'success')}
                          />
                          {token.archivedAt && (
                            <span className="text-gray-500">
                              • Archived {formatElapsedTime(new Date(token.archivedAt).getTime())}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Restore Button */}
                      <button
                        onClick={() => handleRestore(token.tokenAddress)}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm whitespace-nowrap flex-shrink-0"
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ArchiveManager;

